import type { LigneDossier, SourceDossiers } from './source-liste.js';

/**
 * Le contrat n'a pas de route transversale pour la facturation ni pour les documents : les listes de D08 et D09 agrègent PAR DOSSIER,
 * sur les routes existantes. Ce module liste tous les dossiers (en suivant le curseur) puis lit chacun, quelques-uns à la fois.
 */
const PAGES_MAX = 50;
const CONCURRENCE = 4;

export async function listerTousLesDossiers(source: SourceDossiers, options: { pagesMax?: number } = {}): Promise<LigneDossier[]> {
  const pagesMax = options.pagesMax ?? PAGES_MAX;
  const dossiers: LigneDossier[] = [];
  const vus = new Set<string>();
  let curseur: string | undefined;
  for (let page = 0; page < pagesMax; page += 1) {
    const lue = await source.lister(curseur);
    dossiers.push(...lue.items);
    if (lue.nextCursor === null || vus.has(lue.nextCursor)) break;
    vus.add(lue.nextCursor);
    curseur = lue.nextCursor;
  }
  return dossiers;
}

export type LectureDossier<T> = { ok: true; dossier: LigneDossier; valeur: T } | { ok: false; dossier: LigneDossier; erreur: unknown };

/** Lit chaque dossier, `concurrence` à la fois, dans l'ordre des dossiers. L'échec d'un dossier n'emporte pas les autres. */
export async function lireParDossier<T>(
  dossiers: readonly LigneDossier[],
  lire: (dossier: LigneDossier) => Promise<T>,
  options: { concurrence?: number } = {},
): Promise<LectureDossier<T>[]> {
  const resultats: LectureDossier<T>[] = new Array(dossiers.length);
  let suivant = 0;
  const travailleur = async (): Promise<void> => {
    for (;;) {
      const i = suivant++;
      const dossier = dossiers[i];
      if (dossier === undefined) return;
      try {
        resultats[i] = { ok: true, dossier, valeur: await lire(dossier) };
      } catch (erreur) {
        resultats[i] = { ok: false, dossier, erreur };
      }
    }
  };
  const n = Math.max(1, Math.min(options.concurrence ?? CONCURRENCE, dossiers.length));
  await Promise.all(Array.from({ length: n }, travailleur));
  return resultats;
}
